import { useMutation, useQueryClient } from "react-query";

import { updateGoalStatus } from "api/goals";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface PutGoalOnHoldPayload {
  goalId: number;
  clientId: string;
  rationale: string;
}

/**
 * Custom hook to put a goal on hold.
 *
 * @param options - Optional configuration for success and error handling.
 * @returns An object containing the putGoalOnHold function, loading state, and other mutation info.
 */
const usePutGoalOnHold = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: putGoalOnHold,
    isLoading: isPuttingGoalOnHold,
    ...rest
  } = useMutation(
    ({ goalId, rationale }: PutGoalOnHoldPayload) =>
      updateGoalStatus(goalId, { new_status: "on-hold", rationale }),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Goal put on hold" });

        queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);

        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to put goal on hold";
        showToast({ message: errorMessage, errorState: true });

        onError(error);
      },
      ...otherOptions,
    }
  );

  return {
    putGoalOnHold,
    isPuttingGoalOnHold,
    ...rest,
  };
};

export default usePutGoalOnHold;
